import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Swal from "sweetalert2";
import { useSearchParams } from "react-router-dom";
import { ApproveVendorProfile, GetVendorProfiles, SendVendorCredentials, UpdateVendorProfile } from "../../common/CommonApi";

export const VendorManage = () => {
  const [searchParams] = useSearchParams();
  const [vendor, setVendor] = useState({});
  const [modules, setModules] = useState([]);
  const id = searchParams.get("id");

  useEffect(() => {
    getProfile();
  }, []);

  const getProfile = async () => {
    const data = await GetVendorProfiles(id);
    setVendor(data.data.data);
    setModules(data.data.data.permissions || []);
  };

  const hasUploadedDocs = Boolean(
    vendor.accountNumber ||
      vendor.bankName
      // vendor.legalName ||
      // vendor.govtId ||
      // vendor.tradeLicense ||
      // vendor.cancelledCheque
  );

  const getDocsStatus = () => {
    if (vendor.isDocs) return { label: "VERIFIED", status: "approved" };
    if (vendor.isDocsPending) return { label: "PENDING REVIEW", status: "review" };
    if (hasUploadedDocs) return { label: "UPLOADED", status: "uploaded" };
    return { label: "NOT SUBMITTED", status: "pending" };
  };

  const docsStatus = getDocsStatus();

  const handleCheckbox = (module) => {
    if (modules.includes(module)) {
      setModules(modules.filter((m) => m !== module));
    } else {
      setModules([...modules, module]);
    }
  };

  const handleMarkDocsPending = async () => {
    if (vendor.isDocsPending) {
      Swal.fire("Already Pending", "Documents are already marked as pending review.", "info");
      return;
    }
    if (vendor.isDocs) {
      Swal.fire("Already Verified", "Documents are already verified.", "info");
      return;
    }
    if (!hasUploadedDocs) {
      Swal.fire("No Documents", "Vendor has not uploaded any documents yet.", "warning");
      return;
    }
    const data = await ApproveVendorProfile(id, { isDocsPending: true });
    if (data?.status === 200) {
      await getProfile();
      Swal.fire("Marked Pending", "Documents marked as pending review.", "success");
    }
  };

  const handleApproveDocuments = async () => {
    if (vendor.isDocs) {
      Swal.fire("Already Verified", "Documents are already verified for this vendor.", "info");
      return;
    }
    if (!vendor.isDocsPending && !hasUploadedDocs) {
      Swal.fire("No Documents", "Vendor has not submitted documents yet.", "warning");
      return;
    }
    const data = await ApproveVendorProfile(id, { isDocs: true });
    if (data?.status === 200) {
      await getProfile();
      Swal.fire("Documents Approved", "Vendor documents have been verified successfully.", "success");
    }
  };

  const handleSendCredentials = async () => {
    if (vendor.hasLoginAccount) {
      Swal.fire("Already Sent", "Login credentials have already been sent to this vendor.", "info");
      return;
    }
    const data = await SendVendorCredentials(id);
    await getProfile();
    if (data?.status === 201) {
      Swal.fire("Already Sent", "Login credentials were already sent to this vendor.", "info");
      return;
    }
    if (data?.status === 200) {
      Swal.fire(
        "Credentials Sent",
        "Login email and password have been sent to the vendor. They can now log in and upload documents.",
        "success"
      );
    }
  };

  const handleApproveVendor = async () => {
    if (vendor.accountStatus === "approved") {
      Swal.fire("Already Approved", "This vendor is already approved.", "info");
      return;
    }
    if (!vendor.hasLoginAccount) {
      Swal.fire(
        "Credentials Not Sent",
        "Please send login credentials to the vendor first.",
        "warning"
      );
      return;
    }
    if (!vendor.isDocs) {
      Swal.fire(
        "Documents Not Verified",
        "Please approve the vendor documents before activating the account.",
        "warning"
      );
      return;
    }
    if (modules.length === 0) {
      Swal.fire("No Modules", "Please assign at least one module before approving.", "warning");
      return;
    }
    const data = await ApproveVendorProfile(id, { permissions: modules });
    await getProfile();
    if (data?.status === 400) {
      Swal.fire("Cannot Approve", data?.data?.message || "Unable to approve vendor.", "error");
      return;
    }
    if (data?.status === 200) {
      Swal.fire("Vendor Approved", "The vendor can now log in with full module access.", "success");
    }
  };

  const handleUnderReview = async () => {
    if (vendor.accountStatus === "under_review") {
      Swal.fire("Already Under Review", "This vendor is already under review.", "info");
      return;
    }
    if (vendor.accountStatus === "approved") {
      Swal.fire("Already Approved", "This vendor is already approved.", "info");
      return;
    }
    await UpdateVendorProfile(id, {
      accountStatus: "under_review",
      isActive: false,
    });
    await getProfile();
    Swal.fire("Under Review", "The vendor profile is now under review.", "info");
  };

  return (
    <Container>
      <Card>
        <Title>Vendor Review & Approval</Title>
        <Subtitle>Review documents, verify uploads, and activate vendor account.</Subtitle>

        <InfoGrid>
          <InfoItem>
            <Label>Full Name</Label>
            <Value>{vendor.fullname}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Email</Label>
            <Value>{vendor.email}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Phone</Label>
            <Value>{vendor.phone}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Company Name</Label>
            <Value>{vendor.companyName}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Category</Label>
            <Value>{vendor.category}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Address</Label>
            <Value>{vendor.address}</Value>
          </InfoItem>
        </InfoGrid>

        <StatusSection>
          <StatusTitle>Account Status:</StatusTitle>
          <StatusBadge
            status={
              vendor.accountStatus === "approved"
                ? "approved"
                : vendor.accountStatus === "credentials_sent"
                ? "uploaded"
                : vendor.accountStatus === "under_review"
                ? "review"
                : "pending"
            }
          >
            {vendor.accountStatus
              ? vendor.accountStatus.replace(/_/g, " ").toUpperCase()
              : "PENDING"}
          </StatusBadge>
        </StatusSection>

        <Divider />

        <Section>
          <SectionTitle>Step 1 — Send Login Credentials</SectionTitle>
          <SectionHint>
            Send email and temporary password so the vendor can log in and upload their documents.
          </SectionHint>
          <StatusSection>
            <StatusTitle>Login Account:</StatusTitle>
            <StatusBadge status={vendor.hasLoginAccount ? "approved" : "pending"}>
              {vendor.hasLoginAccount ? "CREATED" : "NOT CREATED"}
            </StatusBadge>
          </StatusSection>
          <ButtonGroup>
            <CredentialsButton
              onClick={handleSendCredentials}
              disabled={vendor.hasLoginAccount}
            >
              {vendor.hasLoginAccount ? "Credentials Sent" : "Send Login Credentials"}
            </CredentialsButton>
          </ButtonGroup>
        </Section>

        <Divider />

        <Section>
          <SectionTitle>Step 2 — Document Verification</SectionTitle>
          <SectionHint>
            After the vendor logs in and uploads documents, mark them pending and approve after review.
          </SectionHint>
          <StatusSection>
            <StatusTitle>Documents Status:</StatusTitle>
            <StatusBadge status={docsStatus.status}>{docsStatus.label}</StatusBadge>
          </StatusSection>
          <ButtonGroup>
            <PendingButton
              onClick={handleMarkDocsPending}
              disabled={vendor.isDocsPending || vendor.isDocs || !hasUploadedDocs}
            >
              Mark Documents Pending
            </PendingButton>
            <DocsApproveButton
              onClick={handleApproveDocuments}
              disabled={vendor.isDocs}
            >
              {vendor.isDocs ? "Documents Verified" : "Approve Documents"}
            </DocsApproveButton>
          </ButtonGroup>
        </Section>

        <Divider />

        <Section>
          <SectionTitle>Step 3 — Approve Vendor for Login</SectionTitle>
          <SectionHint>
            After documents are verified, assign modules and approve the vendor for full dashboard access. No email is sent at this step.
          </SectionHint>
          <PermissionsGrid>
            {["dashboard", "products", "orders", "analytics"].map((module) => (
              <CheckboxLabel key={module}>
                <input
                  type="checkbox"
                  checked={modules.includes(module)}
                  onChange={() => handleCheckbox(module)}
                  disabled={vendor.accountStatus === "approved"}
                />
                {module.charAt(0).toUpperCase() + module.slice(1)}
              </CheckboxLabel>
            ))}
          </PermissionsGrid>
          <ButtonGroup>
            <UnderReviewButton onClick={handleUnderReview}>Mark as Under Review</UnderReviewButton>
            <SaveButton
              onClick={handleApproveVendor}
              disabled={vendor.accountStatus === "approved"}
            >
              {vendor.accountStatus === "approved" ? "Vendor Approved" : "Approve Vendor for Login"}
            </SaveButton>
          </ButtonGroup>
        </Section>
      </Card>
    </Container>
  );
};

const Container = styled.div`
  background: #f8fafc;
  padding: 30px;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 0;
    min-height: auto;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 30px;
  max-width: 100%;
  margin: auto;

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  color: #2c3e50;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: #666;
  margin-bottom: 25px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  background: #f5f7fa;
  padding: 12px 15px;
  border-radius: 8px;
  font-size: 15px;
`;

const Label = styled.span`
  display: block;
  color: #555;
  font-weight: 500;
  margin-bottom: 4px;
`;

const Value = styled.span`
  color: #111;
`;

const Section = styled.div`
  margin-top: 25px;
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  font-size: 18px;
  margin-bottom: 6px;
`;

const SectionHint = styled.p`
  color: #888;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin: 28px 0 0;
`;

const PermissionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 10px;
`;

const CheckboxLabel = styled.label`
  background: #f8f9fb;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  &:hover {
    background: #eef9f6;
  }
`;

const BaseButton = styled.button`
  padding: 12px 20px;
  border: none;
  color: white;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  transition: 0.3s;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CredentialsButton = styled(BaseButton)`
  background: #8e44ad;
  &:hover:not(:disabled) {
    background: #7d3c98;
  }
`;

const PendingButton = styled(BaseButton)`
  background: #f39c12;
  &:hover:not(:disabled) {
    background: #e67e22;
  }
`;

const DocsApproveButton = styled(BaseButton)`
  background: #3498db;
  &:hover:not(:disabled) {
    background: #2980b9;
  }
`;

const SaveButton = styled(BaseButton)`
  background: #1abc9c;
  &:hover:not(:disabled) {
    background: #16a085;
  }
`;

const UnderReviewButton = styled(BaseButton)`
  background: #95a5a6;
  &:hover:not(:disabled) {
    background: #7f8c8d;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-top: 20px;

  @media (max-width: 480px) {
    flex-direction: column;

    button { width: 100%; }
  }
`;

const StatusSection = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusTitle = styled.span`
  font-weight: 500;
  color: #444;
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  background: ${({ status }) =>
    status === "approved"
      ? "#d1f2eb"
      : status === "review"
      ? "#fcf3cf"
      : status === "uploaded"
      ? "#dbeafe"
      : "#f5b7b1"};
  color: ${({ status }) =>
    status === "approved"
      ? "#16a085"
      : status === "review"
      ? "#b9770e"
      : status === "uploaded"
      ? "#1d4ed8"
      : "#922b21"};
`;
